import { TaxOfficePicker } from "@/components/TaxOfficePicker";
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Pencil, Store, Warehouse, ChevronDown, Users as UsersIcon, Phone, ShoppingBag, UserPlus, Check, Building2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { TR_CITIES } from "@/lib/tr-cities";
import TaxPortalLookup from "@/components/TaxPortalLookup";

const PROVINCES = Object.keys(TR_CITIES).sort((a, b) => a.localeCompare(b, "tr"));
const REQUIRED = { store: 2, warehouse: 3 };
const needFor = (types) => (types && types.length ? Math.max(...types.map((t) => REQUIRED[t] || 0)) : 0);
const GOREV_OPTIONS = [
  "Mağaza Müdürü", "Müdür Yardımcısı", "Depo Şefi", "Depo Sorumlusu",
  "Kasiyer", "Reyon Görevlisi", "Sevkiyat Sorumlusu", "Satın Alma",
  "Muhasebe", "Güvenlik", "Diğer",
];
const emptyContact = () => ({ name: "", phone: "", role: "" });
const padContacts = (list, need) => {
  const out = [...(list || [])];
  while (out.length < (need || 0)) out.push(emptyContact());
  return out;
};
const emptyForm = {
  name: "", types: ["store"], can_wholesale: false, linked_warehouse_id: "",
  province: "", district: "", phone: "", authorized_person: "", address: "",
  tax_office: "", tax_no: "", tax_verified_at: "", email: "",
  sectors: [],
  is_active: true, contacts: padContacts([], 2),
};

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);
  const [summaries, setSummaries] = useState({});
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [pending, setPending] = useState([]);
  const [sectorOpts, setSectorOpts] = useState([]);
  useEffect(() => { api.get("/sectors").then((r) => setSectorOpts(r.data || [])).catch(() => {}); }, []);
  const toggleFormSector = (code) => setForm((f) => ({ ...f, sectors: (f.sectors || []).includes(code) ? f.sectors.filter((x) => x !== code) : [...(f.sectors || []), code] }));

  const load = () => {
    api.get("/locations").then((r) => {
      setLocations(r.data);
      r.data.forEach((l) => {
        api.get(`/locations/${l.id}/summary`).then((s) => setSummaries((m) => ({ ...m, [l.id]: s.data }))).catch(() => {});
      });
    }).catch(() => {});
    api.get("/locations/pending").then((r) => setPending(r.data)).catch(() => setPending([]));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm, contacts: padContacts([], 2) }); setOpen(true); };
  const openEdit = (l) => {
    const types = l.types && l.types.length ? l.types : [l.type];
    setEditing(l);
    setForm({
      name: l.name, types, can_wholesale: !!l.can_wholesale,
      linked_warehouse_id: l.linked_warehouse_id || "",
      province: l.province || "", district: l.district || "", phone: l.phone || "",
      authorized_person: l.authorized_person || "", address: l.address || "",
      tax_office: l.tax_office || "", tax_no: l.tax_no || "", tax_verified_at: l.tax_verified_at || "", email: l.email || "",
      sectors: l.sectors || [],
      is_active: l.is_active !== false, contacts: padContacts(l.contacts || [], needFor(types)),
    });
    setOpen(true);
  };

  const toggleType = (t) => setForm((f) => {
    let types = f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t];
    if (types.length === 0) types = [t];
    return { ...f, types, contacts: padContacts(f.contacts, needFor(types)) };
  });
  const setContact = (i, key, val) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));
  const addContact = () => setForm((f) => ({ ...f, contacts: [...f.contacts, emptyContact()] }));
  const rmContact = (i) => setForm((f) => {
    if (f.contacts.length <= needFor(f.types)) return f;
    return { ...f, contacts: f.contacts.filter((_, idx) => idx !== i) };
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error("Lokasyon adı zorunlu");
    if (!form.types.length) return toast.error("En az bir tip seçin (Mağaza / Depo)");
    if (!isAdmin) {
      if (!form.phone.trim()) return toast.error("Telefon zorunlu");
      if (!form.address.trim()) return toast.error("Adres zorunlu");
      if (!form.province) return toast.error("İl zorunlu");
      if (!form.district) return toast.error("İlçe zorunlu");
      if (!form.authorized_person.trim()) return toast.error("Yetkili kişi zorunlu");
    }
    const need = needFor(form.types);
    const clean = form.contacts.filter((c) => c.name.trim() && c.phone.trim() && c.role.trim());
    if (clean.length < need) return toast.error(`Bu lokasyon için en az ${need} kişi bilgisi (ad, telefon, görev) zorunlu`);
    const payload = { ...form, name: form.name.trim(), contacts: clean };
    try {
      if (editing) {
        await api.put(`/locations/${editing.id}`, payload);
        toast.success("Lokasyon güncellendi");
      } else {
        const { data } = await api.post("/locations", payload);
        toast.success(data?.message || "Lokasyon oluşturuldu");
      }
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const fetchCompany = async () => {
    try {
      const { data } = await api.get("/settings/company-profile");
      if (!data || !Object.keys(data).some((k) => (data[k] || "").toString().trim())) {
        toast.error("Firma bilgisi tanımlı değil — Ayarlar > Firma Bilgileri'nden bir kez girin.");
        return;
      }
      setForm((f) => ({
        ...f,
        tax_office: data.tax_office || f.tax_office,
        tax_no: data.tax_no || f.tax_no,
        tax_verified_at: "",
        email: data.email || f.email,
        phone: data.phone || f.phone,
        address: data.address || f.address,
        province: data.province || f.province,
        district: data.district || f.district,
        authorized_person: data.authorized_person || f.authorized_person,
      }));
      toast.success("Firma bilgileri forma dolduruldu");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Firma bilgisi getirilemedi"); }
  };

  const approveLoc = async (id) => {
    try { await api.post(`/locations/${id}/approve`); toast.success("Lokasyon onaylandı ve aktifleştirildi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const rejectLoc = async (id) => {
    try { await api.post(`/locations/${id}/reject`, {}); toast.success("Lokasyon reddedildi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/locations/${toDelete.id}`);
      toast.success("Lokasyon silindi"); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setToDelete(null);
  };

  const need = needFor(form.types);

  return (
    <div className="space-y-4 fade-in" data-testid="locations-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-brand-green" />
          <div><p className="overline">Yönetim</p><h1 className="font-heading text-2xl font-bold text-slate-900">Lokasyonlar</h1></div>
        </div>
        <Button data-testid="add-location-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
          <Plus className="mr-2 h-4 w-4" /> Yeni Lokasyon
        </Button>
      </div>

      <p className="text-sm text-slate-500">Mağaza ve depolar. Depo için 3, mağaza için 2 iletişim kişisi (ad, telefon, görev) zorunludur; dilerseniz daha fazla ekleyebilirsiniz. Yönetici dışı roller lokasyon oluşturduğunda Yönetici onayına düşer.</p>

      {isAdmin && pending.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="location-pending-panel">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-amber-800"><Clock className="h-4 w-4" /> Onay Bekleyen Lokasyonlar ({pending.length})</p>
          <div className="space-y-2">
            {pending.map((p) => (
              <div key={p.id} data-testid={`pending-loc-${p.id}`} className="flex items-center justify-between gap-2 rounded-md bg-white px-3 py-2 ring-1 ring-inset ring-amber-100">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">{p.name} <span className="text-[11px] font-normal text-slate-400">· {p.type_label}</span></p>
                  <p className="truncate text-[11px] text-slate-500">{[p.district, p.province].filter(Boolean).join(" / ")}{p.authorized_person ? ` · ${p.authorized_person}` : ""}{p.created_by ? ` · ${p.created_by}` : ""}</p>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <Button data-testid={`pending-approve-${p.id}`} onClick={() => approveLoc(p.id)} size="sm" className="h-8 bg-brand-green text-white hover:bg-brand-green-600"><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Onayla</Button>
                  <Button data-testid={`pending-reject-${p.id}`} onClick={() => rejectLoc(p.id)} size="sm" variant="outline" className="h-8 border-red-300 text-red-600 hover:bg-red-50"><XCircle className="mr-1 h-3.5 w-3.5" /> Reddet</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {locations.map((l) => (
          <div key={l.id} data-testid={`location-card-${l.id}`} className="rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <span className={`flex h-9 w-9 items-center justify-center rounded-md ${(l.types || [l.type]).includes("warehouse") && (l.types || [l.type]).includes("store") ? "bg-indigo-100 text-indigo-600" : (l.types || [l.type]).includes("warehouse") ? "bg-orange-100 text-orange-600" : "bg-emerald-100 text-emerald-600"}`}>
                  {(l.types || [l.type]).includes("warehouse") && (l.types || [l.type]).includes("store") ? <Building2 className="h-5 w-5" /> : (l.types || [l.type]).includes("warehouse") ? <Warehouse className="h-5 w-5" /> : <Store className="h-5 w-5" />}
                </span>
                <div>
                  <p className="font-heading font-semibold text-slate-900">{l.name}</p>
                  <p className="text-[11px] font-medium text-slate-500">{l.type_label}{l.code ? ` · ${l.code}` : ""}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button data-testid={`location-edit-${l.id}`} onClick={() => openEdit(l)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`location-delete-${l.id}`} onClick={() => setToDelete(l)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {l.can_wholesale
                ? <span className="inline-flex items-center gap-1 rounded-sm bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-700"><ShoppingBag className="h-3 w-3" /> Toptan</span>
                : <span className="inline-flex items-center gap-1 rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">Toptan kapalı</span>}
              {(l.types || [l.type]).includes("store") && l.linked_warehouse_name && (
                <span data-testid={`location-linkedwh-${l.id}`} className="inline-flex items-center gap-1 rounded-sm bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700"><Warehouse className="h-3 w-3" /> {l.linked_warehouse_name}</span>
              )}
            </div>

            {(l.province || l.district || l.phone || l.address) && (
              <div className="mt-2 space-y-0.5 text-xs text-slate-500">
                {(l.province || l.district) && <p className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {[l.district, l.province].filter(Boolean).join(" / ")}</p>}
                {l.phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {l.phone}</p>}
                {l.address && <p className="truncate pl-[18px]">{l.address}</p>}
              </div>
            )}

            {l.contacts?.length > 0 && (
              <div className="mt-2 rounded-md bg-slate-50 p-2 ring-1 ring-inset ring-slate-100">
                <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase text-slate-400"><UsersIcon className="h-3 w-3" /> İletişim ({l.contacts.length})</p>
                {l.contacts.slice(0, 3).map((c, i) => (
                  <p key={i} className="truncate text-[11px] text-slate-600"><b>{c.name}</b> · {c.role} · {c.phone}</p>
                ))}
              </div>
            )}

            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
              <div><p className="tabular text-base font-bold text-slate-800">{summaries[l.id]?.sku_count ?? "—"}</p><p className="text-[10px] text-slate-400">SKU</p></div>
              <div><p className="tabular text-base font-bold text-slate-800">{summaries[l.id] ? Math.round(summaries[l.id].total_units) : "—"}</p><p className="text-[10px] text-slate-400">birim</p></div>
              <div><p className={`tabular text-base font-bold ${(summaries[l.id]?.low_stock || 0) > 0 ? "text-amber-600" : "text-slate-800"}`}>{summaries[l.id]?.low_stock ?? "—"}</p><p className="text-[10px] text-slate-400">düşük stok</p></div>
            </div>
            <div className="mt-2 flex items-center gap-1.5 text-xs text-slate-500">
              <UsersIcon className="h-3.5 w-3.5" /> {l.user_count || 0} personel
              {l.status === "pending" && <span className="ml-auto rounded-sm bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">onay bekliyor</span>}
              {l.status === "rejected" && <span className="ml-auto rounded-sm bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700">reddedildi</span>}
              {l.status !== "pending" && l.status !== "rejected" && !l.is_active && <span className="ml-auto rounded-sm bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">pasif</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="location-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Lokasyonu Düzenle" : "Yeni Lokasyon"}</DialogTitle>
            <DialogDescription>Mağaza veya depo bilgilerini girin. Depo için 3, mağaza için 2 iletişim kişisi zorunludur.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Lokasyon Adı *</Label><Input data-testid="location-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Örn: Merkez Mağaza" /></div>

            <div>
              <Label>Tip * <span className="text-[11px] font-normal text-slate-400">(birden fazla seçilebilir — ör. hem Mağaza hem Depo)</span></Label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[["store", "Mağaza", Store], ["warehouse", "Depo", Warehouse]].map(([val, lbl, Icon]) => (
                  <button key={val} type="button" data-testid={`location-type-${val}`} onClick={() => toggleType(val)}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${form.types.includes(val) ? "border-brand-green bg-emerald-50 text-brand-green" : "border-input bg-white text-slate-600 hover:bg-slate-50"}`}>
                    <Icon className="h-4 w-4" /> {lbl}
                    {form.types.includes(val) && <Check className="ml-auto h-4 w-4" />}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 ring-1 ring-inset ring-slate-200">
              <input type="checkbox" data-testid="location-wholesale" checked={form.can_wholesale} onChange={(e) => setForm({ ...form, can_wholesale: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
              <ShoppingBag className="h-4 w-4 text-purple-500" /> Bu lokasyon toptan satış yapabilir
            </label>

            {sectorOpts.length > 0 && (
              <div data-testid="location-sectors">
                <Label className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-brand-green" /> Sektörler <span className="text-[11px] font-normal text-slate-400">(bu lokasyon hangi sektör panellerinde görünsün; boş = tüm sektörler)</span></Label>
                <div className="mt-1 flex flex-wrap gap-1.5 rounded-md border border-input p-2">
                  {sectorOpts.map((s) => {
                    const on = (form.sectors || []).includes(s.code);
                    return (
                      <button key={s.code} type="button" data-testid={`location-sector-${s.code}`} onClick={() => toggleFormSector(s.code)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-brand-green bg-brand-green/5 text-brand-green" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color || "#94a3b8" }} /> {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {form.types.includes("store") && (
              <div>
                <Label className="flex items-center gap-1.5"><Warehouse className="h-4 w-4 text-orange-500" /> Bağlı Depo <span className="text-[11px] font-normal text-slate-400">(sipariş ikmali bu depodan yapılır; kendisi depo ise boş bırakılabilir)</span></Label>
                <div className="relative mt-1">
                  <select data-testid="location-linked-warehouse" value={form.linked_warehouse_id}
                    onChange={(e) => setForm({ ...form, linked_warehouse_id: e.target.value })}
                    className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    <option value="">Seçin</option>
                    {locations.filter((l) => (l.types || [l.type]).includes("warehouse") && l.is_active !== false && l.id !== editing?.id).map((l) => (
                      <option key={l.id} value={l.id}>{l.name}{l.code ? ` · ${l.code}` : ""}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>İl *</Label>
                <div className="relative mt-1">
                  <select data-testid="location-province" value={form.province}
                    onChange={(e) => setForm({ ...form, province: e.target.value, district: "" })}
                    className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
                    <option value="">Seçin</option>
                    {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
              <div>
                <Label>İlçe *</Label>
                <div className="relative mt-1">
                  <select data-testid="location-district" value={form.district} disabled={!form.province}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                    className="h-10 w-full appearance-none rounded-md border border-input bg-white px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green disabled:opacity-50">
                    <option value="">Seçin</option>
                    {(TR_CITIES[form.province] || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>
              </div>
            </div>

            <div><Label>Sabit Telefon *</Label><Input data-testid="location-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" placeholder="0212 000 00 00" /></div>
            <div><Label>Açık Adres *</Label><Input data-testid="location-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Yetkili Kişi *</Label><Input data-testid="location-authorized" value={form.authorized_person} onChange={(e) => setForm({ ...form, authorized_person: e.target.value })} className="mt-1" placeholder="Sorumlu ad soyad" /></div>
              <div><Label>E-posta</Label><Input data-testid="location-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="lokasyon@firma.com" /></div>
              <div><Label>Vergi Dairesi</Label><TaxOfficePicker testid="location-taxoffice" value={form.tax_office} onChange={(v) => setForm({ ...form, tax_office: v })} /></div>
              <div><Label>Vergi No (VKN)</Label><Input data-testid="location-taxno" value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value.replace(/\D/g, ""), tax_verified_at: "" })} className="mt-1 tabular" maxLength={10} placeholder="10 hane" /></div>
              <TaxPortalLookup taxNo={form.tax_no} type="corporate" mode="validate"
                verifiedAt={form.tax_verified_at}
                onVerified={(ts) => setForm((f) => ({ ...f, tax_verified_at: ts }))} testid="location-tax-portal" />
            </div>

            <div className="flex items-center justify-between rounded-md bg-sky-50 px-3 py-2 ring-1 ring-inset ring-sky-100">
              <p className="flex items-center gap-1.5 text-xs text-sky-700"><Building2 className="h-4 w-4" /> Firma bilgilerini tek kaynaktan çekin (Ayarlar &gt; Firma Bilgileri).</p>
              <Button data-testid="location-fetch-company" type="button" onClick={fetchCompany} size="sm" variant="outline" className="h-8 border-sky-300 bg-white text-sky-700 hover:bg-sky-100">
                <Building2 className="mr-1 h-3.5 w-3.5" /> Firma Getir
              </Button>
            </div>

            {/* Contacts */}
            <div className="rounded-md ring-1 ring-inset ring-slate-200 p-3">
              <div className="mb-2 flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><UsersIcon className="h-4 w-4 text-brand-green" /> İletişim Kişileri <span className="text-[11px] font-normal text-slate-400">(en az {need})</span></Label>
                <Button data-testid="location-add-contact" type="button" onClick={addContact} size="sm" variant="outline" className="h-7 border-slate-300 text-xs"><UserPlus className="mr-1 h-3.5 w-3.5" /> Kişi Ekle</Button>
              </div>
              <div className="space-y-2">
                {form.contacts.map((c, i) => (
                  <div key={i} data-testid={`location-contact-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-1.5">
                    <Input data-testid={`contact-name-${i}`} value={c.name} onChange={(e) => setContact(i, "name", e.target.value)} placeholder="Ad Soyad *" className="h-8 text-xs" />
                    <Input data-testid={`contact-phone-${i}`} value={c.phone} onChange={(e) => setContact(i, "phone", e.target.value)} placeholder="Telefon *" className="h-8 text-xs" />
                    <div className="relative">
                      <select data-testid={`contact-role-${i}`} value={c.role} onChange={(e) => setContact(i, "role", e.target.value)}
                        className="h-8 w-full appearance-none rounded-md border border-input bg-white px-2 pr-6 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green">
                        <option value="">Görev seç *</option>
                        {GOREV_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    </div>
                    <button type="button" data-testid={`contact-remove-${i}`} onClick={() => rmContact(i)} disabled={form.contacts.length <= need}
                      className="rounded-sm p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {editing && (
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="location-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                Aktif
              </label>
            )}
            <Button data-testid="location-submit" onClick={save} className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {editing ? "Kaydet" : "Oluştur"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="location-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Lokasyon silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" silinecek. Bu işleme atanmış personel varsa silinemez.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="location-delete-cancel">İptal</AlertDialogCancel>
            <AlertDialogAction data-testid="location-delete-confirm" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
